import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, UserCheck, Building2, Users, Target, Settings,
  LogOut, ShieldCheck, ChevronRight, Bell, Radio, Sparkles,
  User, Lock, X, KeyRound, Eye, EyeOff, Menu, Briefcase, Crown, MessageSquare,
  BadgeDollarSign, History, Globe
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { managerAPI } from '../utils/api';

const ManagerLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [managerInfo, setManagerInfo] = useState({
    name: 'Executive Manager',
    user_id: 'MGR...',
    email: '',
    country: 'India',
    bds_count: 0
  });

  useEffect(() => {
    const fetchManagerInfo = async () => {
      try {
        const storedUser = sessionStorage.getItem('managerUser');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            setManagerInfo(prev => ({
              ...prev,
              name: `${parsed.first_name || ''} ${parsed.last_name || ''}`.trim() || 'Executive Manager',
              user_id: parsed.user_id || parsed.xoxo_id || '10001',
              email: parsed.email || '',
              country: parsed.country || 'India'
            }));
          } catch (e) {}
        }

        const res = await managerAPI.getProfile();
        if (res.data?.manager) {
          const mgr = res.data.manager;
          setManagerInfo({
            name: `${mgr.first_name || ''} ${mgr.last_name || ''}`.trim() || 'Executive Manager',
            user_id: mgr.user_id || mgr.xoxo_id || '10001',
            email: mgr.email || '',
            country: mgr.country || 'India',
            bds_count: mgr.bds_count || 0
          });
        }
      } catch (err) {
        console.error("Failed to fetch manager info:", err);
      }
    };
    fetchManagerInfo();
  }, []);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={19} />, path: '/manager/dashboard' },
    { id: 'bds', label: 'BD Team', icon: <UserCheck size={19} />, path: '/manager/bds' },
    { id: 'agencies', label: 'Agencies', icon: <Building2 size={19} />, path: '/manager/agencies' },
    { id: 'hosts', label: 'Hosts', icon: <Users size={19} />, path: '/manager/hosts' },
    { id: 'users', label: 'Country Users', icon: <ShieldCheck size={19} />, path: '/manager/users' },
    { id: 'rooms', label: 'Live Rooms', icon: <Radio size={19} />, path: '/manager/rooms' },
    { id: 'items', label: 'Items & VIP', icon: <Sparkles size={19} />, path: '/manager/items' },
    { id: 'salaries', label: 'Salaries & Payouts', icon: <BadgeDollarSign size={19} />, path: '/manager/salaries' },
    { id: 'performance', label: 'Targets & Progress', icon: <Target size={19} />, path: '/manager/performance' },
    { id: 'support', label: 'Help & Complaints', icon: <MessageSquare size={19} />, path: '/manager/support' },
    { id: 'audit-logs', label: 'Audit Logs', icon: <History size={19} />, path: '/manager/audit-logs' },
    { id: 'settings', label: 'Account Settings', icon: <Settings size={19} />, path: '/manager/settings' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('managerToken');
    sessionStorage.removeItem('managerUser');
    window.location.href = '/manager/login';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }
    setLoading(true);
    try {
      await managerAPI.changePassword({ 
        oldPassword: passwordData.oldPassword, 
        newPassword: passwordData.newPassword 
      });
      setShowPasswordModal(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password updated successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#030712] text-slate-200 font-['Outfit'] overflow-x-hidden">
      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 inset-y-0 left-0 z-50 h-screen w-64 md:w-72 bg-[#0c0a1f] border-r border-indigo-500/10 backdrop-blur-xl flex flex-col transition-transform duration-300 overflow-hidden
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Top Header & Profile Card (Fixed at top) */}
        <div className="shrink-0">
          {/* Logo Header */}
          <div className="p-4 md:p-5 flex items-center justify-between border-b border-indigo-500/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-tr from-indigo-600 via-purple-600 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <Crown size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-sm font-black text-white tracking-tight uppercase">XOXO <span className="text-indigo-400">MANAGER</span></h1>
                <p className="text-[8px] text-indigo-300/70 font-bold tracking-widest uppercase">Executive Hierarchy</p>
              </div>
            </div>
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Manager Profile Card Pill */}
          <div className="mx-3 mt-3 p-3 rounded-2xl bg-white/[0.03] border border-indigo-500/15 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-xl pointer-events-none"></div>
            <div className="flex items-center gap-2.5 relative z-10">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold text-xs shrink-0">
                MGR
              </div>
              <div className="overflow-hidden min-w-0">
                <h4 className="text-xs font-bold text-white truncate">{managerInfo.name}</h4>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="px-1.5 py-0.2 rounded-full bg-indigo-500/15 text-indigo-300 text-[9px] font-mono font-bold border border-indigo-500/20">
                    {managerInfo.user_id}
                  </span>
                  <span className="flex items-center gap-1 px-1.5 py-0.2 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] font-bold border border-emerald-500/20">
                    <Globe size={9} /> {managerInfo.country}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Navigation Menu (Never pushes footer off screen) */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1 min-h-0 custom-scrollbar">
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl font-semibold text-xs transition-all duration-200 cursor-pointer ${
                  active 
                    ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white shadow-md shadow-indigo-600/30 border border-indigo-400/30' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {active && <ChevronRight size={14} />}
              </button>
            );
          })}
        </nav>

        {/* Footer / Logout (ALWAYS visible and pinned at bottom) */}
        <div className="p-3 border-t border-indigo-500/10 shrink-0 bg-[#0c0a1f]">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-bold text-xs text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 hover:border-rose-500/40 transition-colors cursor-pointer"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 h-20 bg-[#030712]/80 backdrop-blur-xl border-b border-indigo-500/10 px-6 md:px-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)} 
              className="md:hidden p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5"
            >
              <Menu size={24} />
            </button>
            <div className="hidden sm:block">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-400">Executive Portal</span>
              <h2 className="text-lg font-black text-white capitalize">
                {menuItems.find(m => m.path === location.pathname)?.label || 'Manager Portal'}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Country Lock Badge */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs font-semibold text-emerald-300">
              <Globe size={14} className="text-emerald-400" />
              <span>Assigned Country: <strong>{managerInfo.country}</strong></span>
              <span className="text-[10px] bg-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-400 uppercase font-bold tracking-wider">Locked</span>
            </div>

            {/* Password Modal Button */}
            <button 
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20 text-xs font-bold text-indigo-200 hover:text-white transition-all cursor-pointer"
            >
              <KeyRound size={15} className="text-indigo-400" />
              <span className="hidden md:inline">Change Password</span>
            </button>

            {/* Direct Sign Out in Top Navbar */}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-xs font-bold text-rose-300 hover:text-white transition-all cursor-pointer"
              title="Sign Out"
            >
              <LogOut size={14} className="text-rose-400" />
              <span className="hidden md:inline">Sign Out</span>
            </button>

            {/* Manager Profile Header Badge */}
            <div className="flex items-center gap-2.5 pl-2 border-l border-white/10">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-md shadow-indigo-500/20">
                {managerInfo.name.charAt(0).toUpperCase()}
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-xs font-bold text-white leading-tight">{managerInfo.name}</p>
                <p className="text-[10px] text-indigo-400 font-mono font-semibold">{managerInfo.user_id}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content Body */}
        <main className="flex-1 p-6 md:p-10 max-w-7xl w-full mx-auto">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-indigo-500/20 rounded-[32px] p-8 max-w-md w-full shadow-2xl relative">
            <button 
              onClick={() => setShowPasswordModal(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                <Lock size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Change Password</h3>
                <p className="text-xs text-slate-400">Update your Manager portal credentials</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPass.old ? 'text' : 'password'}
                    required
                    value={passwordData.oldPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, old: !showPass.old })}
                    className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300"
                  >
                    {showPass.old ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPass.new ? 'text' : 'password'}
                    required
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    placeholder="At least 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, new: !showPass.new })}
                    className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300"
                  >
                    {showPass.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showPass.confirm ? 'text' : 'password'}
                    required
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500 text-sm"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, confirm: !showPass.confirm })}
                    className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300"
                  >
                    {showPass.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-xs font-bold text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-xs font-bold text-white shadow-lg shadow-indigo-600/30 hover:opacity-90 disabled:opacity-50 cursor-pointer"
                >
                  {loading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerLayout;
